import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, ease } from "./primitives";
import { OF_FEATURES, TERMINAL_URL } from "./orderFlowData";
import { SmartTapeMock } from "./SmartTapeMock";

const Visual = ({ f }) => {
  if (f.tape) return <SmartTapeMock />;
  if (f.phone)
    return (
      <div className="flex justify-center bg-[#0A0B0D] py-8">
        <div className="w-[230px] overflow-hidden rounded-[28px] border border-white/15 bg-black p-2 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]">
          <img src={f.img} alt={f.title} className="block h-[440px] w-full rounded-[22px] object-cover object-top" />
        </div>
      </div>
    );
  return <img src={f.img} alt={f.title} width={1600} height={900} className="block h-auto w-full" loading="lazy" />;
};

const TabList = ({ active, setActive }) => (
  <div role="tablist" className="-mx-6 flex gap-1 overflow-x-auto px-6 pb-2 lg:mx-0 lg:flex-col lg:gap-0 lg:overflow-visible lg:px-0 lg:pb-0">
    {OF_FEATURES.map((f, i) => {
      const on = f.id === active;
      return (
        <button
          key={f.id}
          role="tab"
          aria-selected={on}
          data-testid={`of-tab-${f.id}`}
          onClick={() => setActive(f.id)}
          className={`group relative flex shrink-0 items-center gap-4 whitespace-nowrap border px-4 py-3 text-left font-mono text-xs uppercase tracking-[0.16em] transition-[color,background-color,border-color] duration-300 lg:border-x-0 lg:border-t-0 lg:border-b lg:px-0 lg:py-5 lg:text-sm ${on ? "border-white/25 text-[#ECEBE4] lg:border-white/[0.08]" : "border-white/[0.06] text-[#5A6170] hover:text-[#9DA3AE] lg:border-white/[0.06]"}`}
        >
          <span className={`hidden w-6 lg:inline ${on ? "text-[#BACD1C]" : "text-white/20"}`}>{String(i + 1).padStart(2, "0")}</span>
          {f.tab}
          {on && <motion.span layoutId="of-tab-bar" className="absolute bottom-[-1px] left-0 hidden h-px w-full bg-[#ECEBE4]/70 lg:block" />}
        </button>
      );
    })}
  </div>
);

export const OrderFlowExplorer = () => {
  const [active, setActive] = useState(OF_FEATURES[0].id);
  const f = OF_FEATURES.find((x) => x.id === active);
  return (
    <section id="order-flow" data-testid="of-explorer" className="relative bg-[#0A0A0A] py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <Reveal className="max-w-3xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">The free terminal · what's inside</p>
          <h2 className="mt-4 font-display text-3xl font-semibold leading-[1.02] tracking-[-0.03em] text-[#ECEBE4] md:text-5xl">
            Order flow from real executed trades, <span className="text-[#7D8390]">not estimates.</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-[280px_1fr] lg:gap-16">
          <TabList active={active} setActive={setActive} />
          <>
            <motion.div className="min-w-0" key={f.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease }} data-testid={`of-panel-${f.id}`}>
              <div className="premium-card overflow-hidden border border-white/[0.08]">
                <Visual f={f} />
              </div>
              <div className="mt-8 grid gap-8 md:grid-cols-[1.2fr_1fr]">
                <div>
                  <h3 className="font-display text-2xl font-semibold tracking-[-0.03em] text-[#ECEBE4] md:text-3xl">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#9DA3AE]">{f.body}</p>
                  <a href={TERMINAL_URL} data-testid={`of-open-${f.id}`} className="group mt-6 inline-flex items-center gap-2 border-b border-white/30 pb-1 font-mono text-xs uppercase tracking-[0.18em] text-[#ECEBE4] transition-colors hover:border-[#BACD1C] hover:text-[#BACD1C]">
                    Open in terminal <ArrowUpRight size={14} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </a>
                </div>
                <ul className="space-y-3 border-l border-white/[0.08] pl-6">
                  {f.points.map((p) => (
                    <li key={p} className="flex gap-3 font-mono text-xs leading-relaxed text-[#9DA3AE]">
                      <span className="mt-[7px] h-1 w-1 shrink-0 bg-[#BACD1C]/70" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </>
        </div>
      </div>
    </section>
  );
};
